/**
 * Take-Home Pay tile (BUILD-SPEC.md §3.1) — the first tile, built to exercise
 * the whole shell: it reads its state from the URL fragment, computes through
 * the deterministic tax engine, shows every line with its citation, and offers
 * a worked example (§2 principle 6). The other Pillar 1 tiles in Phase 5 follow
 * this exact pattern.
 */
import { Money } from "../engine/money";
import { evaluateTaxes, type TaxInput, type TaxResult } from "../engine/tax";
import type { DeductionMode } from "../engine/tax/types";
import type { FilingStatus } from "../data/schemas";
import { el, option } from "../ui/dom";
import { field, parseNonNegative, pct, tryExampleButton } from "../ui/form";
import { resultCard, type BreakdownLine } from "../ui/resultCard";
import { rememberShared } from "./profileSync";
import type { SituationStore } from "../profile/situation";
import type { TileContext, TileDefinition } from "./types";

const FILING_STATUSES: { value: FilingStatus; label: string }[] = [
  { value: "single", label: "Single" },
  { value: "married_jointly", label: "Married filing jointly" },
  { value: "married_separately", label: "Married filing separately" },
  { value: "head_of_household", label: "Head of household" },
  { value: "qualifying_surviving_spouse", label: "Qualifying surviving spouse" },
];

const DEDUCTION_MODES: { value: DeductionMode; label: string }[] = [
  { value: "auto", label: "Larger of standard / itemized" },
  { value: "standard", label: "Standard deduction" },
  { value: "itemized", label: "Itemized (big four)" },
];

interface Fields {
  fs: FilingStatus;
  st: string; // state code, or "" for federal-only
  wages: number;
  other: number;
  adjustments: number;
  dm: DeductionMode;
  local: string[]; // selected local add-on ids
}

const EXAMPLE: Fields = {
  fs: "single",
  st: "ca",
  wages: 85000,
  other: 0,
  adjustments: 0,
  dm: "auto",
  local: [],
};

function isFilingStatus(v: string): v is FilingStatus {
  return FILING_STATUSES.some((f) => f.value === v);
}
function isDeductionMode(v: string): v is DeductionMode {
  return DEDUCTION_MODES.some((d) => d.value === v);
}

function readFields(
  params: URLSearchParams,
  defaultState: string,
  profile: SituationStore,
): Fields {
  const fsRaw = params.get("fs");
  const dmRaw = params.get("dm");
  const stRaw = params.get("st");
  return {
    // Precedence: URL fragment > session profile > built-in default.
    fs: fsRaw && isFilingStatus(fsRaw) ? fsRaw : (profile.get("filingStatus") ?? "single"),
    st: stRaw !== null ? stRaw : (profile.get("stateCode") ?? defaultState),
    wages: params.has("w")
      ? parseNonNegative(params.get("w"), 0)
      : (profile.get("annualIncome") ?? 0),
    other: parseNonNegative(params.get("oi"), 0),
    adjustments: parseNonNegative(params.get("adj"), 0),
    dm: dmRaw && isDeductionMode(dmRaw) ? dmRaw : "auto",
    local: (params.get("loc") ?? "").split(",").filter((s) => s.length > 0),
  };
}

function writeFields(f: Fields): URLSearchParams {
  const p = new URLSearchParams();
  p.set("fs", f.fs);
  p.set("st", f.st);
  p.set("w", String(f.wages));
  if (f.other > 0) p.set("oi", String(f.other));
  if (f.adjustments > 0) p.set("adj", String(f.adjustments));
  if (f.dm !== "auto") p.set("dm", f.dm);
  if (f.local.length > 0) p.set("loc", f.local.join(","));
  return p;
}

function buildBreakdown(result: TaxResult, locale: string): BreakdownLine[] {
  const fmt = (m: Money): string => m.format(locale);
  const lines: BreakdownLine[] = [
    { label: "Gross income", value: fmt(result.grossIncome) },
    {
      label: `Federal income tax (${result.federal.deduction.kind} deduction)`,
      value: fmt(result.federal.incomeTax),
      citation: result.federal.citation,
    },
    {
      label: "Social Security (FICA)",
      value: fmt(result.fica.socialSecurity),
      citation: result.fica.citation,
    },
    { label: "Medicare (FICA)", value: fmt(result.fica.medicare), citation: result.fica.citation },
  ];
  if (result.fica.additionalMedicare.greaterThan(0)) {
    lines.push({
      label: "Additional Medicare",
      value: fmt(result.fica.additionalMedicare),
      citation: result.fica.citation,
    });
  }
  if (result.state && result.state.incomeTax.greaterThan(0)) {
    lines.push({
      label: `${result.state.jurisdictionName} income tax`,
      value: fmt(result.state.incomeTax),
      citation: result.state.citation,
    });
  }
  for (const line of result.local.lines) {
    lines.push({
      label: `${line.name} local tax`,
      value: fmt(line.tax),
      citation: result.local.citation,
    });
  }
  lines.push({ label: "Total tax", value: fmt(result.totals.totalTax), emphasis: true });
  lines.push({ label: "Effective rate", value: pct(result.totals.effectiveRate) });
  lines.push({ label: "Marginal rate (next dollar)", value: pct(result.totals.marginalRate) });
  return lines;
}

export function mountTakeHome(ctx: TileContext): void {
  const { root, data } = ctx;
  root.replaceChildren();

  if (!data) {
    root.append(el("p", { class: "tile-error", text: "Tax data could not be loaded." }));
    return;
  }
  const federal = data.federal();
  const fica = data.fica();
  if (!federal || !fica) {
    root.append(
      el("div", {
        class: "verify-banner",
        attrs: { role: "alert" },
        text: "Federal tax data is unavailable, verify before relying on any figure.",
      }),
    );
    return;
  }

  // `data`, `federal`, and `fica` are non-null past the guards above; capture
  // them as locals so the nested closures keep the narrowed (non-null) types.
  const bundled = data;
  const fed = federal;
  const ficaData = fica;
  const codes = bundled.stateCodes();
  const defaultState = codes.includes("ca") ? "ca" : (codes[0] ?? "");
  let fields = readFields(ctx.params, defaultState, ctx.profile);

  // --- Controls ---
  const fsSelect = el(
    "select",
    { name: "fs", attrs: { "aria-label": "Filing status" } },
    ...FILING_STATUSES.map((s) => option(s.value, s.label, s.value === fields.fs)),
  );

  const stSelect = el(
    "select",
    { name: "st", attrs: { "aria-label": "State" } },
    option("", "No state tax modeled", fields.st === ""),
    ...codes.map((code) => {
      const j = bundled.state(code);
      return option(code, j ? j.name : code.toUpperCase(), code === fields.st);
    }),
  );
  // Make the element's value authoritative for the intended state, so a later
  // collect() reads the deep-linked state (not a default) regardless of
  // option-attribute timing.
  stSelect.value = fields.st;

  const wagesInput = el("input", {
    type: "number",
    name: "w",
    min: 0,
    step: 1000,
    value: fields.wages,
    attrs: { "aria-label": "Annual wages", inputmode: "decimal" },
  });
  const otherInput = el("input", {
    type: "number",
    name: "oi",
    min: 0,
    step: 500,
    value: fields.other,
    attrs: { "aria-label": "Other income", inputmode: "decimal" },
  });
  const adjInput = el("input", {
    type: "number",
    name: "adj",
    min: 0,
    step: 500,
    value: fields.adjustments,
    attrs: { "aria-label": "Pre-tax adjustments", inputmode: "decimal" },
  });
  const dmSelect = el(
    "select",
    { name: "dm", attrs: { "aria-label": "Deduction method" } },
    ...DEDUCTION_MODES.map((d) => option(d.value, d.label, d.value === fields.dm)),
  );

  const localContainer = el("div", { class: "local-addons" });
  const resultContainer = el("div", { class: "tile-result", attrs: { "aria-live": "polite" } });

  // A residence-based local tax (Maryland's county tax) is mandatory and set by
  // where you live, so it is a REQUIRED single-select — exactly one applies. Make
  // sure a valid county is selected, defaulting to the shard's default, so the
  // displayed Maryland tax always includes the county portion (never a stale or
  // missing one). Opt-in locals (NYC, Columbus) keep their multi-checkbox set.
  function resolveLocal(): void {
    const state = fields.st ? bundled.state(fields.st) : null;
    const residence = state?.residenceLocalTax;
    if (!residence) return;
    const valid = new Set((state?.localAddOns ?? []).map((a) => a.id));
    const current = fields.local.find((id) => valid.has(id));
    fields.local = [current ?? residence.defaultId];
  }

  function renderLocalAddOns(): void {
    localContainer.replaceChildren();
    const state = fields.st ? bundled.state(fields.st) : null;
    const addOns = state?.localAddOns ?? [];
    if (addOns.length === 0) return;
    const residence = state?.residenceLocalTax;
    if (residence) {
      const selected = fields.local[0] ?? residence.defaultId;
      const countySelect = el(
        "select",
        { name: "loc-select", attrs: { "aria-label": residence.label } },
        ...addOns.map((a) => option(a.id, a.name, a.id === selected)),
      );
      // Set the value explicitly so the right county is selected regardless of
      // option-attribute timing (a pre-set `selected` on a detached option).
      countySelect.value = selected;
      countySelect.addEventListener("change", recompute);
      localContainer.append(field(residence.label, countySelect));
      return;
    }
    localContainer.append(el("p", { class: "field-group-label", text: "Local taxes" }));
    for (const addOn of addOns) {
      const cb = el("input", {
        type: "checkbox",
        name: `loc-${addOn.id}`,
        checked: fields.local.includes(addOn.id),
        attrs: { "aria-label": addOn.name },
        on: { change: () => recompute() },
      });
      cb.id = `loc-${addOn.id}`;
      localContainer.append(
        el("label", { class: "checkbox" }, cb, el("span", { text: addOn.name })),
      );
    }
  }

  function collect(): void {
    const countySelect = localContainer.querySelector<HTMLSelectElement>(
      "select[name='loc-select']",
    );
    const local = countySelect
      ? countySelect.value
        ? [countySelect.value]
        : []
      : Array.from(localContainer.querySelectorAll<HTMLInputElement>("input:checked")).map((cb) =>
          cb.name.replace(/^loc-/, ""),
        );
    fields = {
      fs: isFilingStatus(fsSelect.value) ? fsSelect.value : "single",
      st: stSelect.value,
      wages: parseNonNegative(wagesInput.value, 0),
      other: parseNonNegative(otherInput.value, 0),
      adjustments: parseNonNegative(adjInput.value, 0),
      dm: isDeductionMode(dmSelect.value) ? dmSelect.value : "auto",
      local,
    };
    resolveLocal();
  }

  function compute(): void {
    const input: TaxInput = {
      filingStatus: fields.fs,
      wages: fields.wages,
      otherIncome: fields.other,
      adjustments: fields.adjustments,
      deductionMode: fields.dm,
      localJurisdictionIds: fields.local,
    };
    const state = fields.st ? (bundled.state(fields.st) ?? undefined) : undefined;
    const result = evaluateTaxes(input, { federal: fed, fica: ficaData, state });

    resultContainer.replaceChildren(
      resultCard({
        label: "Annual take-home pay",
        value: result.totals.takeHome,
        locale: ctx.locale,
        breakdown: buildBreakdown(result, ctx.locale),
        permalink: () => ctx.permalink(writeFields(fields)),
      }),
    );
  }

  function recompute(): void {
    collect();
    renderLocalAddOns();
    ctx.setParams(writeFields(fields));
    rememberShared(ctx.profile, {
      filingStatus: fields.fs,
      stateCode: fields.st,
      annualIncome: fields.wages,
    });
    compute();
  }

  for (const control of [fsSelect, stSelect, dmSelect]) {
    control.addEventListener("change", recompute);
  }
  for (const input of [wagesInput, otherInput, adjInput]) {
    input.addEventListener("input", recompute);
  }

  const tryExample = tryExampleButton(() => {
    fields = { ...EXAMPLE };
    fsSelect.value = fields.fs;
    stSelect.value = fields.st;
    wagesInput.value = String(fields.wages);
    otherInput.value = String(fields.other);
    adjInput.value = String(fields.adjustments);
    dmSelect.value = fields.dm;
    recompute();
  });

  const form = el(
    "form",
    {
      class: "tile-form",
      on: { submit: (e) => e.preventDefault() },
    },
    field("Filing status", fsSelect),
    field("State", stSelect),
    field("Annual wages", wagesInput),
    field("Other income", otherInput),
    field("Pre-tax adjustments", adjInput),
    field("Deduction method", dmSelect),
    localContainer,
    el("div", { class: "tile-form-actions" }, tryExample),
  );

  root.append(form, resultContainer);
  resolveLocal();
  renderLocalAddOns();
  compute();
}

export const takeHomeTile: TileDefinition = {
  id: "take-home",
  title: "Take-Home Pay",
  pillar: "paycheck",
  description: "Your real paycheck after federal, FICA, state, and local taxes, across all states.",
  keywords: ["paycheck", "net pay", "salary", "withholding", "fica", "state tax"],
  status: "ready",
  how: "Your take-home is your gross pay minus four things: federal income tax (the larger of your standard or itemized deduction, then the IRS bracket schedule), FICA (Social Security at 6.2% up to the annual wage base, plus Medicare at 1.45%, and a 0.9% Additional Medicare surtax on high earners), your state income tax, and any local tax that applies.\n\nThe effective rate is your total tax divided by gross income; the marginal rate is the bracket your next dollar lands in. Every line links the IRS or state source it came from.",
  resources: [
    {
      label: "IRS, federal income tax rates",
      url: "https://www.irs.gov/filing/federal-income-tax-rates-and-brackets",
    },
    {
      label: "IRS Publication 15-T (withholding)",
      url: "https://www.irs.gov/pub/irs-pdf/p15t.pdf",
    },
    {
      label: "SSA, Social Security & Medicare tax rates",
      url: "https://www.ssa.gov/oact/progdata/taxRates.html",
    },
  ],
  mount: mountTakeHome,
};
